import React, { useEffect, useState } from "react";
import SideBar from "./SideBar";
import axiosClient from "../axios";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { LocalizationProvider, DateTimePicker, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import loader from "../assets/images/loader.gif";
import { ReactComponent as Picadd } from "../assets/images/newAddPic.svg";
import "../assets/css/CustomBanner.css";
import JoditEditor from "jodit-react";
import DependsOnOfferSearch, { formatOfferOptionLabel } from "../helperComponent/DependsOnOfferSearch";
import { compressImageToTargetSize } from "../utils/imageCompression";
import {
  BANNER_SIZES,
  buildBannerSubmitPayload,
  buildTargetingPayload,
  clampAdSlot,
  CLOSE_BUTTON_POSITIONS,
  DEFAULT_POPUP_CONFIG,
  DEFAULT_TARGETING,
  DISPLAY_FREQUENCIES,
  DISPLAY_MODES,
  getAdSlotOptions,
  getApiErrorMessage,
  getAvailableScreenPositions,
  isTitleSubtitleRequired,
  normalizeTargetingFromApi,
  PACKAGE_INCLUDE_MATCH_OPTIONS,
  REDIRECT_TYPES,
  shouldShowAdSlotDropdown,
  showCompactAdSlotField,
  showInlineDesignFields,
  TRIGGER_TYPES,
  USER_SEGMENTS,
} from "../constants/bannerConfig";

function EditCustomBanner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bannerId = searchParams.get("banner_id");

  const [isLoading, setIsLoading] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    displayMode: "inline",
    popupConfig: DEFAULT_POPUP_CONFIG,
    title: "",
    name: "",
    subtitle: "",
    imageUrl: "",
    backgroundColor: "#4A4A4A",
    buttonText: "Click to redeem",
    buttonColor: "#FFFFFF",
    isActive: true,
    priority: 0,
    bannerSize: "standard",
    adSlot: 1,
    redirectType: "partner",
    customId: "",
    internalRoute: "",
    customUrl: "",
    userSegment: "all",
    isScheduled: false,
    fullBanner: false,
    screenPosition: 1,
  });
  const [dates, setDates] = useState({
    start: null,
    end: null,
  });

  const [targeting, setTargeting] = useState(DEFAULT_TARGETING);
  // Controls whether the "Registered Between" or "Last Login Between" date
  // range is shown/editable. Defaults to "registered".
  const [dateTargetType, setDateTargetType] = useState("registered");
  const [packageDraft, setPackageDraft] = useState({ include: "", exclude: "" });
  const [partnerEarningDraft, setPartnerEarningDraft] = useState({
    partner: null,
    min: "",
    max: "",
  });

  // Draft selection (from the offer search box) for each offer include/exclude
  // list, keyed as "<field>_<include|exclude>", e.g. "offersCompleted_include".
  const [offerDrafts, setOfferDrafts] = useState({});
  // Cache of offerId -> offer option (name/partner) so chips can show a
  // readable label instead of a raw ObjectId.
  const [offerOptionLabels, setOfferOptionLabels] = useState({});

  const redirectTypes = REDIRECT_TYPES;
  const userSegments = USER_SEGMENTS;
  const displayModes = DISPLAY_MODES;
  const closeButtonPositions = CLOSE_BUTTON_POSITIONS;
  const triggerTypes = TRIGGER_TYPES;
  const displayFrequencies = DISPLAY_FREQUENCIES;

  const isInline = formData.displayMode === "inline";
  const isPopup = formData.displayMode === "popup";
  const inlineDesignFields = showInlineDesignFields(formData);
  const availableScreenPositions = getAvailableScreenPositions(formData.displayMode);
  const compactAdSlotField = showCompactAdSlotField(formData);
  const adSlotOptions = getAdSlotOptions(formData.screenPosition);
  const titleSubtitleRequired = isTitleSubtitleRequired(formData);

  const getCountries = () => {
    axiosClient
      .get("/getallCountry")
      .then((res) => {
        setAllCountries(res?.data?.response || []);
      })
      .catch((error) => console.log(error));
  };

  const getPartners = () => {
    axiosClient
      .get("/partnersList")
      .then((res) => {
        setAllPartners(res?.data?.response || []);
      })
      .catch((error) => console.log(error));
  };

  const getBannerDetails = () => {
    if (!bannerId) {
      alert("Banner ID not found");
      navigate("/allcustomBanner");
      return;
    }

    setIsLoading(false);

    axiosClient
      .post("/admin/clickbait/all", {
        page: 0,
        limit: 1000,
        fetchCount: false,
        // Omit isActive → backend returns active-only. 3 = all (active + inactive)
        isActive: 3,
        isScheduled: 3,
        fullBanner: 3,
        redirectType: "",
        screenPosition: "",
        displayMode: "",
        bannerSize: "",
        searchValue: "",
      })
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          const allBanners = res?.data?.response?.totalData || [];
          const banner = allBanners.find((b) => b._id === bannerId);

          if (banner) {
            setFormData({
              displayMode: banner.displayMode || "inline",
              popupConfig: {
                close: {
                  ...DEFAULT_POPUP_CONFIG.close,
                  ...(banner.popupConfig?.close || {}),
                },
                trigger: {
                  ...DEFAULT_POPUP_CONFIG.trigger,
                  ...(banner.popupConfig?.trigger || {}),
                },
                frequency: {
                  ...DEFAULT_POPUP_CONFIG.frequency,
                  ...(banner.popupConfig?.frequency || {}),
                },
              },
              name: banner.name || "",
              title: banner.title || "",
              subtitle: banner.subtitle || "",
              imageUrl: banner.imageUrl || "",
              backgroundColor: banner.backgroundColor || "#4A4A4A",
              buttonText: banner.buttonText || "Click to redeem",
              buttonColor: banner.buttonColor || "#FFFFFF",
              isActive: banner.isActive || false,
              priority: banner.priority || 0,
              bannerSize: banner.bannerSize || "standard",
              adSlot: clampAdSlot(
                banner.screenPosition || 1,
                banner.adSlot ?? 1
              ),
              redirectType: banner.redirectType || "partner",
              customId: banner.customId || "",
              internalRoute: banner.internalRoute || "",
              customUrl: banner.customUrl || "",
              userSegment: banner.userSegment || "all",
              isScheduled: banner.isScheduled || false,
              fullBanner: banner.fullBanner || false,
              screenPosition: banner.screenPosition || 1,
            });

            setImagePreview(banner.imageUrl || "");

            const { targeting: normalizedTargeting, offerOptionLabels: normalizedOfferLabels } =
              normalizeTargetingFromApi(banner.targeting);
            setTargeting(normalizedTargeting);
            setOfferOptionLabels(normalizedOfferLabels);
            // Pick whichever date range tab already has saved data so the
            // existing rule is visible; defaults to "registered" otherwise.
            setDateTargetType(
              normalizedTargeting.lastLoginBetween.from || normalizedTargeting.lastLoginBetween.to
                ? "login"
                : "registered"
            );

            if (banner.start_date) {
              setDates((prev) => ({ ...prev, start: dayjs(banner.start_date) }));
            }
            if (banner.end_date) {
              setDates((prev) => ({ ...prev, end: dayjs(banner.end_date) }));
            }

            if (banner.countries && banner.countries.length > 0) {
              axiosClient.get("/getallCountry").then((countryRes) => {
                const countries = countryRes?.data?.response || [];
                const matchedCountries = countries.filter((c) =>
                  banner.countries.includes(c.alpha2)
                );
                setSelectedCountries(matchedCountries);
              });
            }
          } else {
            alert("Banner not found");
            navigate("/allcustomBanner");
          }
        } else {
          alert(`Error fetching banner: ${res?.data?.message || `statusCode ${res?.data?.statusCode}`}`);
        }
      })
      .catch((error) => {
        console.log(error);
        alert("Error fetching banner details");
        navigate("/allcustomBanner");
      })
      .finally(() => {
        setIsLoading(true);
      });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleRedirectTypeChange = (e) => {
    setFormData({
      ...formData,
      redirectType: e.target.value,
      customId: "",
      internalRoute: "",
      customUrl: "",
    });
  };

  const handlePopupConfigChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      popupConfig: {
        ...prev.popupConfig,
        [section]: {
          ...prev.popupConfig[section],
          [field]: value,
        },
      },
    }));
  };

  const handleDisplayModeChange = (newMode) => {
    setFormData((prev) => {
      const next = { ...prev, displayMode: newMode };
      if (newMode === "popup" && prev.screenPosition === 2) {
        next.screenPosition = 1;
      }
      return next;
    });
  };

  const handleImageChange = (e) => {
    const { value } = e.target;
    setFormData({ ...formData, imageUrl: value });
    setImagePreview(value);
  };

  const handleFileInput = async (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) return;

    setUploadingImage(true);

    let fileToUpload = selectedFile;
    const fileSizeInKb = selectedFile.size / 1024;

    if (fileSizeInKb > 100) {
      try {
        fileToUpload = await compressImageToTargetSize(selectedFile, 100);
      } catch (err) {
        console.log(err);
        alert("Error compressing image");
        setUploadingImage(false);
        return;
      }
    }

    const photo = new FormData();
    photo.append("photo", fileToUpload);

    await axiosClient
      .post(`/uploadImage`, photo)
      .then((res) => {
        const uploadedUrl = res.data.response.url;
        setFormData({ ...formData, imageUrl: uploadedUrl });
        setImagePreview(uploadedUrl);
        alert(
          fileToUpload !== selectedFile
            ? "Image was larger than 100 KB, so it was compressed and uploaded successfully!"
            : "Image uploaded successfully!"
        );
      })
      .catch((err) => {
        console.log(err);
        alert("Error uploading image");
      })
      .finally(() => {
        setUploadingImage(false);
      });
  };

  const handleCountriesChange = (event, newValue) => {
    setSelectedCountries(newValue);
  };

  const handleDateChange = (value, type) => {
    if (type === 1) {
      setDates({ ...dates, start: value });
    } else {
      setDates({ ...dates, end: value });
    }
  };

  const handleTargetingToggle = (e) => {
    setTargeting((prev) => ({ ...prev, enabled: e.target.checked }));
  };

  const handleDateTargetTypeChange = (type) => {
    setDateTargetType(type);
    // Clear out the hidden range so a stale value from before switching
    // tabs doesn't get sent to the backend.
    setTargeting((prev) => ({
      ...prev,
      registeredBetween: type === "registered" ? prev.registeredBetween : { from: null, to: null },
      lastLoginBetween: type === "login" ? prev.lastLoginBetween : { from: null, to: null },
    }));
  };

  const handleTargetingRangeChange = (field, part, value) => {
    setTargeting((prev) => ({
      ...prev,
      [field]: { ...prev[field], [part]: value },
    }));
  };

  const handleTargetingMinMaxChange = (field, part, value) => {
    setTargeting((prev) => ({
      ...prev,
      [field]: { ...prev[field], [part]: value },
    }));
  };

  const handlePackageDraftChange = (type, value) => {
    setPackageDraft((prev) => ({ ...prev, [type]: value }));
  };

  const handleAddPackageItem = (type) => {
    const value = packageDraft[type].trim();
    if (!value) return;

    setTargeting((prev) => {
      if (prev.packages[type].includes(value)) return prev;
      return {
        ...prev,
        packages: {
          ...prev.packages,
          [type]: [...prev.packages[type], value],
        },
      };
    });
    setPackageDraft((prev) => ({ ...prev, [type]: "" }));
  };

  const handleRemovePackageItem = (type, index) => {
    setTargeting((prev) => ({
      ...prev,
      packages: {
        ...prev.packages,
        [type]: prev.packages[type].filter((_, i) => i !== index),
      },
    }));
  };

  const handlePackageIncludeMatchChange = (value) => {
    setTargeting((prev) => ({
      ...prev,
      packages: { ...prev.packages, include_match: value },
    }));
  };

  const handleOfferIncludeMatchChange = (field, value) => {
    setTargeting((prev) => ({
      ...prev,
      [field]: { ...prev[field], include_match: value },
    }));
  };

  const getPartnerName = (partnerId) =>
    allPartners.find((partner) => String(partner._id) === String(partnerId))?.name || partnerId;

  const handlePartnerEarningDraftChange = (field, value) => {
    setPartnerEarningDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddPartnerEarning = () => {
    if (!partnerEarningDraft.partner?._id) {
      alert("Please select a partner");
      return;
    }

    setTargeting((prev) => ({
      ...prev,
      partnerEarnings: [
        ...prev.partnerEarnings,
        {
          partnerId: partnerEarningDraft.partner._id,
          min: partnerEarningDraft.min,
          max: partnerEarningDraft.max,
        },
      ],
    }));
    setPartnerEarningDraft({ partner: null, min: "", max: "" });
  };

  const handleRemovePartnerEarning = (index) => {
    setTargeting((prev) => ({
      ...prev,
      partnerEarnings: prev.partnerEarnings.filter((_, i) => i !== index),
    }));
  };

  const handleOfferDraftChange = (field, type, selectedOfferId, selectedOption) => {
    setOfferDrafts((prev) => ({
      ...prev,
      [`${field}_${type}`]: { offerId: selectedOfferId, option: selectedOption },
    }));
  };

  const handleAddOfferItem = (field, type) => {
    const draftKey = `${field}_${type}`;
    const draft = offerDrafts[draftKey];
    const offerId = draft?.offerId;
    if (!offerId) return;

    setTargeting((prev) => {
      if (prev[field][type].includes(offerId)) return prev;
      return {
        ...prev,
        [field]: {
          ...prev[field],
          [type]: [...prev[field][type], offerId],
        },
      };
    });

    if (draft?.option) {
      setOfferOptionLabels((prev) => ({ ...prev, [offerId]: draft.option }));
    }

    setOfferDrafts((prev) => ({ ...prev, [draftKey]: { offerId: null, option: null } }));
  };

  const handleRemoveOfferItem = (field, type, index) => {
    setTargeting((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [type]: prev[field][type].filter((_, i) => i !== index),
      },
    }));
  };

  const getOfferChipLabel = (offerId) =>
    formatOfferOptionLabel(offerOptionLabels[offerId] || { _id: offerId, name: offerId });

  const handleSubmit = () => {
    if (isInline) {
      if (!formData.imageUrl || !formData.redirectType) {
        alert("imageUrl and redirectType are required");
        return;
      }
      if (!formData.screenPosition) {
        alert("Please select a Screen Position");
        return;
      }
      if (titleSubtitleRequired && (!formData.title || !formData.subtitle)) {
        alert("Title and subtitle are required for standard banners");
        return;
      }
    } else {
      if (!formData.imageUrl || !formData.redirectType) {
        alert("imageUrl and redirectType are required for popup banners");
        return;
      }
      if (!formData.screenPosition) {
        alert("Please select a Screen Position");
        return;
      }
      if (formData.screenPosition === 2) {
        alert("Popups cannot use Earn Screen Block 2. Please select a different screen position.");
        return;
      }
      if (formData.popupConfig?.frequency?.displayFrequency === "custom") {
        const { frequencyCapCount, frequencyCapPeriodHours } = formData.popupConfig.frequency;
        if (!frequencyCapCount || frequencyCapCount <= 0 || !frequencyCapPeriodHours || frequencyCapPeriodHours <= 0) {
          alert("Custom frequency requires Frequency Cap Count and Period (hours) greater than 0");
          return;
        }
      }
    }

    if (formData.redirectType === "partner" && !formData.customId) {
      alert("Please enter Partner ID");
      return;
    }
    if (formData.redirectType === "offer" && !formData.customId) {
      alert("Please enter Offer ID");
      return;
    }
    if (formData.redirectType === "internal" && !formData.internalRoute) {
      alert("Please enter Internal Route");
      return;
    }
    if (formData.redirectType === "custom" && !formData.customUrl) {
      alert("Please enter Custom URL");
      return;
    }
    if (formData.redirectType === "video" && !formData.customUrl) {
      alert("Please enter Video URL");
      return;
    }

    if (formData.isScheduled && (!dates.start || !dates.end)) {
      alert("Please select both start and end dates for scheduled banner");
      return;
    }

    setIsLoading(false);

    const countries = selectedCountries.map((country) => country.alpha2);

    const bodyData = buildBannerSubmitPayload(formData, {
      countries,
      startDate: formData.isScheduled && dates.start ? dates.start.toISOString() : null,
      endDate: formData.isScheduled && dates.end ? dates.end.toISOString() : null,
    });

    bodyData.targeting = buildTargetingPayload(targeting);

    axiosClient
      .put(`/admin/clickbait/edit/${bannerId}`, bodyData)
      .then((res) => {
        if (res?.data?.statusCode === 200) {
          alert("Banner updated successfully!");
          navigate("/allcustomBanner");
        } else {
          alert(res?.data?.message || "Error updating banner");
        }
      })
      .catch((error) => {
        console.log(error);
        alert(getApiErrorMessage(error, "Error updating banner"));
      })
      .finally(() => {
        setIsLoading(true);
      });
  };

  useEffect(() => {
    getCountries();
    getPartners();
    getBannerDetails();
  }, []);

  return (
    <>
      <SideBar handleSideBar={true} activeTab={"AllCustomBanner"} />
      <div className="banner-create-screen">
        <div className="banner-header">
          <h2>Edit Custom Banner</h2>
          <button className="back-button" onClick={() => navigate("/allcustomBanner")}>
            ← Back to List
          </button>
        </div>

        {isLoading ? (
          <div className="banner-form-container">
            {/* Banner Type */}
            <div className="form-section">
              <div className="section-header">Banner Type</div>
              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">
                    Display Mode <span className="required-star">*</span>
                  </label>
                  <div className="radio-group-v4" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                    {displayModes.map((mode) => (
                      <label key={mode.value} className="radio-option-v4" style={{ cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="displayMode"
                          checked={formData.displayMode === mode.value}
                          onChange={() => handleDisplayModeChange(mode.value)}
                        />
                        <span>{mode.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="info-text">
                    {isInline
                      ? "Inline banners appear at a fixed slot on the selected screen."
                      : "Popup banners overlay the entire screen with a full-screen image."}
                  </p>
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="form-section">
              <div className="section-header">Basic Information</div>
              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="form-input"
                    placeholder="Enter banner name (internal reference)"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  <p className="info-text">
                    Internal name to identify this banner in the admin list. Not shown to end users.
                  </p>
                </div>
              </div>
              {inlineDesignFields && (
                <>
                  <div className="form-row single">
                    <div className="form-group">
                      <label className="form-label">
                        Title {titleSubtitleRequired && <span className="required-star">*</span>}
                      </label>
                      <input
                        type="text"
                        name="title"
                        className="form-input"
                        placeholder="Enter banner title (e.g., Amazon)"
                        value={formData.title}
                        onChange={handleInputChange}
                      />
                      {!titleSubtitleRequired && (
                        <p className="info-text">Optional for compact (ads-style) banners — internal notes only.</p>
                      )}
                    </div>
                  </div>

                  <div className="form-row single">
                    <div className="form-group">
                      <label className="form-label">
                        Subtitle {titleSubtitleRequired && <span className="required-star">*</span>}
                      </label>
                      <JoditEditor
                        placeholder="Enter banner subtitle (e.g., Get a Rs.10 voucher)"
                        className="form-input"
                        value={formData.subtitle}
                        onChange={(newContent) => setFormData({ ...formData, subtitle: newContent })}
                      />
                      {!titleSubtitleRequired && (
                        <p className="info-text">Optional for compact (ads-style) banners — internal notes only.</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">
                    Banner Image <span className="required-star">*</span>
                  </label>

                  <div className="image-upload-section">
                    <label htmlFor="bannerImage" className="upload-label">
                      <Picadd className="upload-icon" />
                      <span className="upload-text">
                        {uploadingImage ? "Uploading..." : "Click to upload image"}
                      </span>
                      <input
                        type="file"
                        id="bannerImage"
                        name="photo"
                        onChange={handleFileInput}
                        accept="image/*"
                        className="file-input"
                        disabled={uploadingImage}
                      />
                    </label>

                    <div className="or-divider">
                      <span>OR</span>
                    </div>

                    <input
                      type="text"
                      name="imageUrl"
                      className="form-input"
                      placeholder="Or paste image URL here"
                      value={formData.imageUrl}
                      onChange={handleImageChange}
                    />
                  </div>

                  {imagePreview && (
                    <div className="image-preview-container">
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                      <button
                        className="remove-image-btn"
                        onClick={() => {
                          setFormData({ ...formData, imageUrl: "" });
                          setImagePreview("");
                        }}
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isInline && (
                <div className="form-row single">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="fullBanner"
                      className="checkbox-input"
                      checked={formData.fullBanner}
                      onChange={handleInputChange}
                    />
                    <label className="checkbox-label">Full Image</label>
                    <p className="info-text">
                      When enabled, the image alone is the entire banner visual (title, subtitle, and button fields are hidden).
                    </p>
                  </div>
                </div>
              )}

              {inlineDesignFields && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Background Color</label>
                      <div className="color-preview-box">
                        <input
                          type="color"
                          name="backgroundColor"
                          className="color-input"
                          value={formData.backgroundColor}
                          onChange={handleInputChange}
                        />
                        <div className="color-preview" style={{ background: formData.backgroundColor }}></div>
                        <span className="color-code">{formData.backgroundColor}</span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Button Color</label>
                      <div className="color-preview-box">
                        <input
                          type="color"
                          name="buttonColor"
                          className="color-input"
                          value={formData.buttonColor}
                          onChange={handleInputChange}
                        />
                        <div className="color-preview" style={{ background: formData.buttonColor }}></div>
                        <span className="color-code">{formData.buttonColor}</span>
                      </div>
                    </div>
                  </div>

                  <div className="form-row single">
                    <div className="form-group">
                      <label className="form-label">Button Text</label>
                      <input
                        type="text"
                        name="buttonText"
                        className="form-input"
                        placeholder="Enter button text"
                        value={formData.buttonText}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <input
                    type="number"
                    name="priority"
                    className="number-input"
                    placeholder="Higher number = Higher priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  />
                  <p className="info-text">Higher priority banners appear first within the same screen position</p>
                </div>
              </div>
            </div>
            <div className="form-section">
              <div className="section-header">Display Settings</div>

              {isInline && (
                <div className="form-row single">
                  <div className="form-group">
                    <label className="form-label">Banner Size</label>
                    <div className="autocomplete-wrapper">
                      <Autocomplete
                        disablePortal
                        value={BANNER_SIZES.find((option) => option.value === formData.bannerSize) || BANNER_SIZES[0]}
                        onChange={(event, newValue) => {
                          const newSize = newValue?.value || "standard";
                          setFormData((prev) => ({
                            ...prev,
                            bannerSize: newSize,
                            ...(newSize === "compact"
                              ? { adSlot: clampAdSlot(prev.screenPosition, prev.adSlot ?? 1) }
                              : {}),
                          }));
                        }}
                        options={BANNER_SIZES}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => <TextField {...params} placeholder="Select banner size" />}
                      />
                    </div>
                    <p className="info-text">Compact uses the shorter ads-style banner card.</p>
                  </div>
                </div>
              )}

              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">
                    Screen Position <span className="required-star">*</span>
                  </label>
                  <div className="autocomplete-wrapper">
                    <Autocomplete
                      disablePortal
                      value={availableScreenPositions.find((option) => option.value === formData.screenPosition) || null}
                      onChange={(event, newValue) => {
                        const newPosition = newValue?.value || 1;
                        setFormData((prev) => ({
                          ...prev,
                          screenPosition: newPosition,
                          adSlot: clampAdSlot(newPosition, prev.adSlot ?? 1),
                        }));
                      }}
                      options={availableScreenPositions}
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => <TextField {...params} placeholder="Select screen position" />}
                    />
                  </div>
                  <p className="info-text">
                    {isPopup
                      ? "Select the screen where this popup overlay appears. Earn Screen Block 2 is not available for popups."
                      : "Select where this banner should be displayed. Multiple banners at same position will show as carousel"}
                  </p>
                </div>
              </div>

              {compactAdSlotField && shouldShowAdSlotDropdown(formData.screenPosition) && (
                <div className="form-row single">
                  <div className="form-group">
                    <label className="form-label">Ad Position</label>
                    <div className="autocomplete-wrapper">
                      <Autocomplete
                        disablePortal
                        value={
                          adSlotOptions.find((option) => option.value === formData.adSlot) ||
                          adSlotOptions[0]
                        }
                        onChange={(event, newValue) => {
                          setFormData((prev) => ({
                            ...prev,
                            adSlot: newValue?.value ?? 1,
                          }));
                        }}
                        options={adSlotOptions}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select ad position" />
                        )}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isPopup && (
              <>
                <div className="form-section">
                  <div className="section-header">Popup Close Settings</div>
                  <div className="form-row single">
                    <div className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        className="checkbox-input"
                        checked={formData.popupConfig.close.isClosable}
                        onChange={(e) => handlePopupConfigChange("close", "isClosable", e.target.checked)}
                      />
                      <label className="checkbox-label">User Can Close Popup</label>
                    </div>
                  </div>
                  <div className="form-row">
                    {formData.popupConfig.close.isClosable && (
                      <>
                        <div className="form-group">
                          <label className="form-label">Close Button Delay (seconds)</label>
                          <input
                            type="number"
                            min="0"
                            className="number-input"
                            value={formData.popupConfig.close.closeDelaySeconds}
                            onChange={(e) => handlePopupConfigChange("close", "closeDelaySeconds", Number(e.target.value) || 0)}
                          />
                          <p className="info-text">X button appears after this many seconds (0 = immediately)</p>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Close Button Position</label>
                          <div className="autocomplete-wrapper">
                            <Autocomplete
                              disablePortal
                              value={closeButtonPositions.find((option) => option.value === formData.popupConfig.close.closeButtonPosition) || null}
                              onChange={(event, newValue) => {
                                handlePopupConfigChange("close", "closeButtonPosition", newValue?.value || "top-right");
                              }}
                              options={closeButtonPositions}
                              getOptionLabel={(option) => option.name}
                              renderInput={(params) => <TextField {...params} placeholder="Select close button position" />}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    <div className="form-group">
                      <label className="form-label">Auto Close (seconds)</label>
                      <input
                        type="number"
                        min="0"
                        className="number-input"
                        value={formData.popupConfig.close.autoCloseSeconds}
                        onChange={(e) => handlePopupConfigChange("close", "autoCloseSeconds", Number(e.target.value) || 0)}
                      />
                      <p className="info-text">0 = never auto-dismiss</p>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="section-header">Popup Trigger Settings</div>
                  <div className="form-row single">
                    <div className="form-group">
                      <label className="form-label">Trigger Type</label>
                      <div className="autocomplete-wrapper">
                        <Autocomplete
                          disablePortal
                          value={triggerTypes.find((option) => option.value === formData.popupConfig.trigger.triggerType) || null}
                          onChange={(event, newValue) => {
                            handlePopupConfigChange("trigger", "triggerType", newValue?.value || "on_load");
                          }}
                          options={triggerTypes}
                          getOptionLabel={(option) => option.name}
                          renderInput={(params) => <TextField {...params} placeholder="Select trigger type" />}
                        />
                      </div>
                    </div>
                  </div>
                  {formData.popupConfig.trigger.triggerType === "delayed" && (
                    <div className="form-row single">
                      <div className="form-group">
                        <label className="form-label">Trigger Delay (seconds)</label>
                        <input
                          type="number"
                          min="0"
                          className="number-input"
                          value={formData.popupConfig.trigger.triggerDelaySeconds}
                          onChange={(e) => handlePopupConfigChange("trigger", "triggerDelaySeconds", Number(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="section-header">Popup Frequency Settings</div>
                  <div className="form-row single">
                    <div className="form-group">
                      <label className="form-label">Display Frequency</label>
                      <div className="autocomplete-wrapper">
                        <Autocomplete
                          disablePortal
                          value={displayFrequencies.find((option) => option.value === formData.popupConfig.frequency.displayFrequency) || null}
                          onChange={(event, newValue) => {
                            handlePopupConfigChange("frequency", "displayFrequency", newValue?.value || "always");
                          }}
                          options={displayFrequencies}
                          getOptionLabel={(option) => option.name}
                          renderInput={(params) => <TextField {...params} placeholder="Select display frequency" />}
                        />
                      </div>
                    </div>
                  </div>
                  {formData.popupConfig.frequency.displayFrequency === "custom" && (
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">
                          Frequency Cap Count <span className="required-star">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          className="number-input"
                          value={formData.popupConfig.frequency.frequencyCapCount}
                          onChange={(e) => handlePopupConfigChange("frequency", "frequencyCapCount", Number(e.target.value) || 0)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Cap Period (hours) <span className="required-star">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          className="number-input"
                          value={formData.popupConfig.frequency.frequencyCapPeriodHours}
                          onChange={(e) => handlePopupConfigChange("frequency", "frequencyCapPeriodHours", Number(e.target.value) || 0)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Min Gap (Minutes)</label>
                        <input
                          type="number"
                          min="0"
                          className="number-input"
                          value={formData.popupConfig.frequency.frequencyMinGap}
                          onChange={(e) => handlePopupConfigChange("frequency", "frequencyMinGap", Number(e.target.value) || 0)}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
            {/* Redirection Settings */}
            <div className="form-section">
              <div className="section-header">Redirection Settings</div>

              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">
                    Redirect Type <span className="required-star">*</span>
                  </label>
                  <select
                    name="redirectType"
                    className="form-input"
                    value={formData.redirectType}
                    onChange={handleRedirectTypeChange}
                  >
                    {redirectTypes.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  {formData.redirectType === "none" && (
                    <p className="info-text">Banner will not be clickable — no redirect action on tap.</p>
                  )}
                </div>
              </div>

              {formData.redirectType === "partner" && (
                <div className="form-row single">
                  <div className="form-group">
                    <label className="form-label">
                      Partner ID <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      name="customId"
                      className="form-input"
                      placeholder="Enter partner ID"
                      value={formData.customId}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              {formData.redirectType === "offer" && (
                <div className="form-row single">
                  <div className="form-group">
                    <label className="form-label">
                      Offer ID <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      name="customId"
                      className="form-input"
                      placeholder="Enter offer ID"
                      value={formData.customId}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              {formData.redirectType === "internal" && (
                <div className="form-row single">
                  <div className="form-group">
                    <label className="form-label">
                      Internal Route <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      name="internalRoute"
                      className="form-input"
                      placeholder="Enter internal route (without slashes)"
                      value={formData.internalRoute}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              {(formData.redirectType === "custom" || formData.redirectType === "video") && (
                <div className="form-row single">
                  <div className="form-group">
                    <label className="form-label">
                      {formData.redirectType === "video" ? "Video URL" : "Custom URL"}{" "}
                      <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      name="customUrl"
                      className="form-input"
                      placeholder="https://example.com"
                      value={formData.customUrl}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Targeting Settings */}
            <div className="form-section">
              <div className="section-header">Targeting Settings</div>

              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">Countries</label>
                  <div className="autocomplete-wrapper">
                    <Autocomplete
                      multiple
                      value={selectedCountries}
                      onChange={handleCountriesChange}
                      options={allCountries}
                      getOptionLabel={(option) => `${option.name} (${option.alpha2})`}
                      renderInput={(params) => <TextField {...params} placeholder="Select countries (Leave empty for all countries)" />}
                    />
                  </div>
                  <p className="info-text">Leave empty to show banner in all countries</p>
                </div>
              </div>

              <div className="form-row single">
                <div className="form-group">
                  <label className="form-label">User Segment</label>
                  <div className="autocomplete-wrapper">
                    <Autocomplete
                      disablePortal
                      value={userSegments.find((option) => option.value === formData.userSegment) || null}
                      onChange={(event, newValue) => {
                        setFormData({ ...formData, userSegment: newValue?.value || "all" });
                      }}
                      options={userSegments}
                      getOptionLabel={(option) => option.name}
                      renderInput={(params) => <TextField {...params} placeholder="Select user segment" />}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Targeting Rules */}
            <div className="form-section">
              <div className="section-header">Advanced Targeting Rules</div>

              <div className="form-row single">
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={targeting.enabled}
                    onChange={handleTargetingToggle}
                  />
                  <label className="checkbox-label">Enable Targeting Rules</label>
                </div>
                <p className="info-text">
                  Every field below is optional — leave it blank to skip that rule. If you don't
                  fill in anything, no targeting rules are sent.
                </p>
              </div>

              {targeting.enabled && (
                <div className="conditional-section">
                  <div className="targeting-subsection">
                    <div className="targeting-subsection-title">Date Filter</div>
                    <div className="radio-group-v4" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      <label className="radio-option-v4" style={{ cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="dateTargetType"
                          checked={dateTargetType === "registered"}
                          onChange={() => handleDateTargetTypeChange("registered")}
                        />
                        <span>Register</span>
                      </label>
                      <label className="radio-option-v4" style={{ cursor: "pointer" }}>
                        <input
                          type="radio"
                          name="dateTargetType"
                          checked={dateTargetType === "login"}
                          onChange={() => handleDateTargetTypeChange("login")}
                        />
                        <span>Login</span>
                      </label>
                    </div>

                    {dateTargetType === "registered" ? (
                      <div className="form-row" style={{ marginTop: "12px" }}>
                        <div className="form-group">
                          <label className="form-label">From</label>
                          <div className="date-picker-wrapper">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={targeting.registeredBetween.from}
                                onChange={(value) =>
                                  handleTargetingRangeChange(
                                    "registeredBetween",
                                    "from",
                                    value ? value.startOf("day") : null
                                  )
                                }
                                renderInput={(params) => <TextField {...params} />}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">To</label>
                          <div className="date-picker-wrapper">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={targeting.registeredBetween.to}
                                onChange={(value) =>
                                  handleTargetingRangeChange(
                                    "registeredBetween",
                                    "to",
                                    value ? value.endOf("day") : null
                                  )
                                }
                                renderInput={(params) => <TextField {...params} />}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="form-row" style={{ marginTop: "12px" }}>
                        <div className="form-group">
                          <label className="form-label">From</label>
                          <div className="date-picker-wrapper">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={targeting.lastLoginBetween.from}
                                onChange={(value) =>
                                  handleTargetingRangeChange(
                                    "lastLoginBetween",
                                    "from",
                                    value ? value.startOf("day") : null
                                  )
                                }
                                renderInput={(params) => <TextField {...params} />}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>
                        <div className="form-group">
                          <label className="form-label">To</label>
                          <div className="date-picker-wrapper">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={targeting.lastLoginBetween.to}
                                onChange={(value) =>
                                  handleTargetingRangeChange(
                                    "lastLoginBetween",
                                    "to",
                                    value ? value.endOf("day") : null
                                  )
                                }
                                renderInput={(params) => <TextField {...params} />}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="targeting-subsection">
                    <div className="targeting-subsection-title">Platform Earnings (Lifetime)</div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Min</label>
                        <input
                          type="number"
                          className="number-input"
                          placeholder="No minimum"
                          value={targeting.platformEarnings.min}
                          onChange={(e) =>
                            handleTargetingMinMaxChange("platformEarnings", "min", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Max</label>
                        <input
                          type="number"
                          className="number-input"
                          placeholder="No maximum"
                          value={targeting.platformEarnings.max}
                          onChange={(e) =>
                            handleTargetingMinMaxChange("platformEarnings", "max", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="targeting-subsection">
                    <div className="targeting-subsection-title">Partner Earnings</div>
                    <p className="info-text">
                      Add one or more partners with an earning range. Multiple entries are combined
                      with AND.
                    </p>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Partner</label>
                        <div className="autocomplete-wrapper">
                          <Autocomplete
                            disablePortal
                            options={allPartners}
                            value={partnerEarningDraft.partner}
                            onChange={(event, newValue) => handlePartnerEarningDraftChange("partner", newValue)}
                            getOptionLabel={(option) => option?.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              String(option?._id) === String(value?._id)
                            }
                            renderInput={(params) => <TextField {...params} placeholder="Select a partner" />}
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Min</label>
                        <input
                          type="number"
                          className="number-input"
                          placeholder="No minimum"
                          value={partnerEarningDraft.min}
                          onChange={(e) => handlePartnerEarningDraftChange("min", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Max</label>
                        <input
                          type="number"
                          className="number-input"
                          placeholder="No maximum"
                          value={partnerEarningDraft.max}
                          onChange={(e) => handlePartnerEarningDraftChange("max", e.target.value)}
                        />
                      </div>
                      <div className="form-group targeting-add-btn-wrapper">
                        <button
                          type="button"
                          className="targeting-add-btn"
                          onClick={handleAddPartnerEarning}
                        >
                          + Add Partner Rule
                        </button>
                      </div>
                    </div>

                    {targeting.partnerEarnings.length > 0 && (
                      <div className="targeting-list">
                        {targeting.partnerEarnings.map((entry, index) => (
                          <div className="targeting-list-item" key={`${entry.partnerId}-${index}`}>
                            <span>
                              <strong>{getPartnerName(entry.partnerId)}</strong> (min: {entry.min || "—"}, max:{" "}
                              {entry.max || "—"})
                            </span>
                            <button
                              type="button"
                              className="targeting-remove-btn"
                              onClick={() => handleRemovePartnerEarning(index)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="targeting-subsection">
                    <div className="targeting-subsection-title">Withdrawal Count (Lifetime)</div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Min</label>
                        <input
                          type="number"
                          className="number-input"
                          placeholder="No minimum"
                          value={targeting.withdrawalCount.min}
                          onChange={(e) =>
                            handleTargetingMinMaxChange("withdrawalCount", "min", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Max</label>
                        <input
                          type="number"
                          className="number-input"
                          placeholder="No maximum"
                          value={targeting.withdrawalCount.max}
                          onChange={(e) =>
                            handleTargetingMinMaxChange("withdrawalCount", "max", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="targeting-subsection">
                    <div className="targeting-subsection-title">Withdrawn Amount (Lifetime)</div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Min</label>
                        <input
                          type="number"
                          className="number-input"
                          placeholder="No minimum"
                          value={targeting.withdrawnAmount.min}
                          onChange={(e) =>
                            handleTargetingMinMaxChange("withdrawnAmount", "min", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Max</label>
                        <input
                          type="number"
                          className="number-input"
                          placeholder="No maximum"
                          value={targeting.withdrawnAmount.max}
                          onChange={(e) =>
                            handleTargetingMinMaxChange("withdrawnAmount", "max", e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="targeting-subsection">
                    <div className="targeting-subsection-title">Packages</div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Include Packages</label>
                        <div className="targeting-chip-input-row">
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Type a package name and click Add"
                            value={packageDraft.include}
                            onChange={(e) => handlePackageDraftChange("include", e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddPackageItem("include");
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="targeting-add-btn"
                            onClick={() => handleAddPackageItem("include")}
                          >
                            Add
                          </button>
                        </div>
                        {targeting.packages.include.length > 0 && (
                          <div className="targeting-chip-wrapper">
                            {targeting.packages.include.map((pkg, index) => (
                              <Chip
                                key={`${pkg}-${index}`}
                                label={pkg}
                                onDelete={() => handleRemovePackageItem("include", index)}
                              />
                            ))}
                          </div>
                        )}
                        {targeting.packages.include.length > 0 && (
                          <div style={{ marginTop: "8px" }}>
                            <label className="form-label">Include Match</label>
                            <div className="radio-group-v4" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                              {PACKAGE_INCLUDE_MATCH_OPTIONS.map((option) => (
                                <label key={option.value} className="radio-option-v4" style={{ cursor: "pointer" }}>
                                  <input
                                    type="radio"
                                    name="packageIncludeMatch"
                                    checked={(targeting.packages.include_match || "any") === option.value}
                                    onChange={() => handlePackageIncludeMatchChange(option.value)}
                                  />
                                  <span>{option.name}</span>
                                </label>
                              ))}
                            </div>
                            <p className="info-text">
                              Any = user has at least one included package. All = user must have every included package.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">Exclude Packages</label>
                        <div className="targeting-chip-input-row">
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Type a package name and click Add"
                            value={packageDraft.exclude}
                            onChange={(e) => handlePackageDraftChange("exclude", e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddPackageItem("exclude");
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="targeting-add-btn"
                            onClick={() => handleAddPackageItem("exclude")}
                          >
                            Add
                          </button>
                        </div>
                        {targeting.packages.exclude.length > 0 && (
                          <div className="targeting-chip-wrapper">
                            {targeting.packages.exclude.map((pkg, index) => (
                              <Chip
                                key={`${pkg}-${index}`}
                                label={pkg}
                                onDelete={() => handleRemovePackageItem("exclude", index)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="info-text">Leave both empty to target all packages.</p>
                  </div>

                  <div className="targeting-subsection">
                    <div className="targeting-subsection-title">Offers Completed</div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Include Offers</label>
                        <div className="targeting-chip-input-row">
                          <div className="autocomplete-wrapper" style={{ flex: 1 }}>
                            <DependsOnOfferSearch
                              key={`offersCompleted-include-${targeting.offersCompleted.include.length}`}
                              offerId={offerDrafts.offersCompleted_include?.offerId}
                              initialOption={offerDrafts.offersCompleted_include?.option}
                              onChange={(selectedOfferId, selectedOption) =>
                                handleOfferDraftChange(
                                  "offersCompleted",
                                  "include",
                                  selectedOfferId,
                                  selectedOption
                                )
                              }
                            />
                          </div>
                          <button
                            type="button"
                            className="targeting-add-btn"
                            onClick={() => handleAddOfferItem("offersCompleted", "include")}
                          >
                            Add
                          </button>
                        </div>
                        {targeting.offersCompleted.include.length > 0 && (
                          <div className="targeting-chip-wrapper">
                            {targeting.offersCompleted.include.map((offerId, index) => (
                              <Chip
                                key={`${offerId}-${index}`}
                                label={getOfferChipLabel(offerId)}
                                onDelete={() => handleRemoveOfferItem("offersCompleted", "include", index)}
                              />
                            ))}
                          </div>
                        )}
                        {targeting.offersCompleted.include.length > 0 && (
                          <div style={{ marginTop: "8px" }}>
                            <label className="form-label">Include Match</label>
                            <div className="radio-group-v4" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                              {PACKAGE_INCLUDE_MATCH_OPTIONS.map((option) => (
                                <label key={option.value} className="radio-option-v4" style={{ cursor: "pointer" }}>
                                  <input
                                    type="radio"
                                    name="offersCompletedIncludeMatch"
                                    checked={(targeting.offersCompleted.include_match || "any") === option.value}
                                    onChange={() => handleOfferIncludeMatchChange("offersCompleted", option.value)}
                                  />
                                  <span>{option.name}</span>
                                </label>
                              ))}
                            </div>
                            <p className="info-text">
                              Any = user has completed at least one included offer. All = user must have completed every included offer.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">Exclude Offers</label>
                        <div className="targeting-chip-input-row">
                          <div className="autocomplete-wrapper" style={{ flex: 1 }}>
                            <DependsOnOfferSearch
                              key={`offersCompleted-exclude-${targeting.offersCompleted.exclude.length}`}
                              offerId={offerDrafts.offersCompleted_exclude?.offerId}
                              initialOption={offerDrafts.offersCompleted_exclude?.option}
                              onChange={(selectedOfferId, selectedOption) =>
                                handleOfferDraftChange(
                                  "offersCompleted",
                                  "exclude",
                                  selectedOfferId,
                                  selectedOption
                                )
                              }
                            />
                          </div>
                          <button
                            type="button"
                            className="targeting-add-btn"
                            onClick={() => handleAddOfferItem("offersCompleted", "exclude")}
                          >
                            Add
                          </button>
                        </div>
                        {targeting.offersCompleted.exclude.length > 0 && (
                          <div className="targeting-chip-wrapper">
                            {targeting.offersCompleted.exclude.map((offerId, index) => (
                              <Chip
                                key={`${offerId}-${index}`}
                                label={getOfferChipLabel(offerId)}
                                onDelete={() => handleRemoveOfferItem("offersCompleted", "exclude", index)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="info-text">
                      Target users who have completed (or not completed) specific offers. Leave
                      both empty to skip this rule.
                    </p>
                  </div>

                  <div className="targeting-subsection">
                    <div className="targeting-subsection-title">Offers Attempted</div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Include Offers</label>
                        <div className="targeting-chip-input-row">
                          <div className="autocomplete-wrapper" style={{ flex: 1 }}>
                            <DependsOnOfferSearch
                              key={`offersAttempted-include-${targeting.offersAttempted.include.length}`}
                              offerId={offerDrafts.offersAttempted_include?.offerId}
                              initialOption={offerDrafts.offersAttempted_include?.option}
                              onChange={(selectedOfferId, selectedOption) =>
                                handleOfferDraftChange(
                                  "offersAttempted",
                                  "include",
                                  selectedOfferId,
                                  selectedOption
                                )
                              }
                            />
                          </div>
                          <button
                            type="button"
                            className="targeting-add-btn"
                            onClick={() => handleAddOfferItem("offersAttempted", "include")}
                          >
                            Add
                          </button>
                        </div>
                        {targeting.offersAttempted.include.length > 0 && (
                          <div className="targeting-chip-wrapper">
                            {targeting.offersAttempted.include.map((offerId, index) => (
                              <Chip
                                key={`${offerId}-${index}`}
                                label={getOfferChipLabel(offerId)}
                                onDelete={() => handleRemoveOfferItem("offersAttempted", "include", index)}
                              />
                            ))}
                          </div>
                        )}
                        {targeting.offersAttempted.include.length > 0 && (
                          <div style={{ marginTop: "8px" }}>
                            <label className="form-label">Include Match</label>
                            <div className="radio-group-v4" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                              {PACKAGE_INCLUDE_MATCH_OPTIONS.map((option) => (
                                <label key={option.value} className="radio-option-v4" style={{ cursor: "pointer" }}>
                                  <input
                                    type="radio"
                                    name="offersAttemptedIncludeMatch"
                                    checked={(targeting.offersAttempted.include_match || "any") === option.value}
                                    onChange={() => handleOfferIncludeMatchChange("offersAttempted", option.value)}
                                  />
                                  <span>{option.name}</span>
                                </label>
                              ))}
                            </div>
                            <p className="info-text">
                              Any = user has attempted at least one included offer. All = user must have attempted every included offer.
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">Exclude Offers</label>
                        <div className="targeting-chip-input-row">
                          <div className="autocomplete-wrapper" style={{ flex: 1 }}>
                            <DependsOnOfferSearch
                              key={`offersAttempted-exclude-${targeting.offersAttempted.exclude.length}`}
                              offerId={offerDrafts.offersAttempted_exclude?.offerId}
                              initialOption={offerDrafts.offersAttempted_exclude?.option}
                              onChange={(selectedOfferId, selectedOption) =>
                                handleOfferDraftChange(
                                  "offersAttempted",
                                  "exclude",
                                  selectedOfferId,
                                  selectedOption
                                )
                              }
                            />
                          </div>
                          <button
                            type="button"
                            className="targeting-add-btn"
                            onClick={() => handleAddOfferItem("offersAttempted", "exclude")}
                          >
                            Add
                          </button>
                        </div>
                        {targeting.offersAttempted.exclude.length > 0 && (
                          <div className="targeting-chip-wrapper">
                            {targeting.offersAttempted.exclude.map((offerId, index) => (
                              <Chip
                                key={`${offerId}-${index}`}
                                label={getOfferChipLabel(offerId)}
                                onDelete={() => handleRemoveOfferItem("offersAttempted", "exclude", index)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="info-text">
                      Target users who have attempted (or not attempted) specific offers. Leave
                      both empty to skip this rule.
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Scheduling Settings */}
            <div className="form-section">
              <div className="section-header">Scheduling Settings</div>

              <div className="form-row single">
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="isScheduled"
                    className="checkbox-input"
                    checked={formData.isScheduled}
                    onChange={handleInputChange}
                  />
                  <label className="checkbox-label">Enable Scheduling</label>
                </div>
              </div>

              {formData.isScheduled && (
                <div className="conditional-section">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Start Date & Time</label>
                      <div className="date-picker-wrapper">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            value={dates.start}
                            onChange={(value) => handleDateChange(value, 1)}
                            renderInput={(params) => <TextField {...params} />}
                          />
                        </LocalizationProvider>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">End Date & Time</label>
                      <div className="date-picker-wrapper">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DateTimePicker
                            value={dates.end}
                            onChange={(value) => handleDateChange(value, 2)}
                            renderInput={(params) => <TextField {...params} />}
                          />
                        </LocalizationProvider>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="form-section">
              <div className="section-header">Status</div>

              <div className="form-row single">
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="isActive"
                    className="checkbox-input"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="checkbox-label">Banner is Active</label>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="submit-button-wrapper">
              <button className="cancel-button" onClick={() => navigate("/allcustomBanner")}>
                Cancel
              </button>
              <button className="submit-button" onClick={handleSubmit}>
                Update Banner
              </button>
            </div>
          </div>
        ) : (
          <div className="loader-container">
            <img src={loader} alt="loader" className="loader-image" />
          </div>
        )}
      </div>
    </>
  );
}

export default EditCustomBanner;